// i18n setup — the one impure i18n seam. Owns the shared Lingui
// instance, catalog loading, locale detection, and activation. This is
// composition at the impure shell, NOT an anti-corruption wrapper:
// components use Lingui's <Trans>/t`` macros directly (the macros must
// be imported from @lingui/* to be extracted), and pure code returns
// MessageDescriptors via `msg`. Locale flows to the tree through
// <I18nProvider i18n={i18n}> (deps via context); activation happens
// only here.
//
// Catalogs: en.po/sv.po are the source (extracted), en.ts/sv.ts are
// compiled, untracked artifacts. The PR that adds a string adds its
// Swedish (`pnpm i18n:sweep`, then fill sv.po); the i18n-catalog gate
// fails otherwise.
//
// Note: imports come first here (not the repo's exports-first house
// style) because the Babel macro pass rejects re-exporting an imported
// binding (`i18n`) before its import statement.

import { i18n, type Messages } from "@lingui/core";
import { compileMessage } from "@lingui/message-utils/compileMessage";
import { readDeviceKey, writeDeviceKey } from "../app/deviceStore.js";
import { messages as en } from "./locales/en.js";
import { messages as sv } from "./locales/sv.js";
import { isLocale, type Locale } from "./supportedLocales.js";

export { isLocale, type Locale };

// A message not yet in a catalog renders from its English source, which is
// raw ICU ("{sources, plural, …}"). Lingui compiles it at runtime only in
// development, so a production build showed the raw text until the next
// i18n sweep. Compile it here in every build.
i18n.setMessagesCompiler(compileMessage);

// Total over the supported-locale table: adding a locale there fails to
// compile here until its compiled catalog is loaded.
const catalogs: Record<Locale, Messages> = { en, sv };
i18n.load(catalogs);

// Dotted `bilda.` namespace (renamed from the legacy `bilda-ui-locale`) so the
// device-store seam globs it into the captured/restored device track. Pre-MVP:
// no migration — a device that stored the legacy key simply re-detects locale
// once (project_migration_squash_at_first_stable).
const LOCALE_STORAGE_KEY = "bilda.locale";

// Impure: a picked locale (persisted) wins over the browser's preferred
// language, so the choice survives reload. Otherwise the audience is
// Swedish-first, so `sv*` picks Swedish; everything else falls back to
// English (the source locale). Called once at the mount.
export const detectLocale = (): Locale => {
    try {
        const stored = readDeviceKey(LOCALE_STORAGE_KEY);
        if (isLocale(stored)) return stored;
    } catch {
        /* storage disabled — fall through to the browser preference */
    }
    return typeof navigator !== "undefined" &&
        navigator.language.startsWith("sv")
        ? "sv"
        : "en";
};

export const activateLocale = (l: Locale): void => {
    i18n.activate(l);
};

// Activate AND persist — the language picker calls this so the choice
// survives reload (mirrors the theme). The I18nProvider re-renders every
// <Trans>/useLingui consumer on activate.
export const setLocale = (l: Locale): void => {
    activateLocale(l);
    try {
        writeDeviceKey(LOCALE_STORAGE_KEY, l);
    } catch {
        /* storage disabled — best-effort persistence only */
    }
};

export { i18n };
